import React, { useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { loadAuthApi } from "../utils/firebaseAuth";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { BASE_URL, customResetPasswordErrorMessages } from "../utils/tools";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { LuChevronLeft } from "react-icons/lu";
import { Spinner } from "@jammable/ui-components/Loader";
import { useT } from "@jammable/ui-core/src/i18n";
import {
  AuthError,
  authErrorText,
  authFieldError,
  authInput,
  authInputError,
  authLink,
  authPrimary,
} from "../components/auth/JamAuth";
const ResetPasswordPage = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { oobCode } = router.query;
  const t = useT();

  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .min(6, t("auth.passwordMin"))
      .required(t("auth.passwordRequired")),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      loadAuthApi()
        .then(({ auth, fb }) =>
          fb.confirmPasswordReset(auth, oobCode, values.password),
        )
        .then(() => {
          toast.success(t("auth.reset.success"));
          router.push("/login");
        })
        .catch((error) => {
          console.error("Error resetting password: ", error);
          setError(
            authErrorText(t, "reset", customResetPasswordErrorMessages, error),
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const title = "Reset Password | Jammable";
  const url = `${BASE_URL}/resetpassword`;

  return (
    <div className="tw-flex tw-w-full tw-justify-center tw-bg-white tw-px-5 tw-pb-16 tw-pt-6 tw-text-jam-ink md:tw-pt-16">
      <NextSeo
        title={title}
        canonical={url}
        openGraph={{ title: title, url: url }}
      />
      <div className="tw-flex tw-w-full tw-max-w-[420px] tw-flex-col tw-gap-4">
        <Link
          href="/login"
          aria-label={t("auth.backToLogin")}
          className="tw-flex tw-h-[42px] tw-w-[42px] tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-ink"
        >
          <LuChevronLeft
            className="tw-h-[18px] tw-w-[18px]"
            strokeWidth={2.5}
          />
        </Link>
        <h1 className="jam-display tw-m-0 tw-mt-3 tw-text-[36px] tw-font-extrabold tw-leading-none md:tw-text-[44px]">
          {t("auth.reset.title")}
        </h1>
        <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted">
          {t("auth.reset.blurb")}
        </p>
        <form
          className="tw-flex tw-w-full tw-flex-col tw-gap-4"
          onChange={() => {
            setError(null);
          }}
          onSubmit={formik.handleSubmit}
        >
          {error && <AuthError>{error}</AuthError>}
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <input
              type="password"
              placeholder={t("auth.reset.newPassword")}
              aria-label={t("auth.reset.newPassword")}
              autoComplete="new-password"
              className={`${authInput} ${formik.touched.password && formik.errors.password ? authInputError : ""}`}
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.password && formik.errors.password && (
              <div className={`${authFieldError} tw-px-5`}>
                {formik.errors.password}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className={authPrimary}>
            {loading ? (
              <Spinner className="tw-border-white tw-border-3" size={4} />
            ) : (
              t("auth.reset.submit")
            )}
          </button>
        </form>
        <div className="tw-text-center tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {t("auth.reset.expired")}{" "}
          <Link href="/forgotpassword" className={authLink}>
            {t("auth.reset.sendNew")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
